'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { ConversationEntry } from '@/types/chat';
function sortConversationsByActivity(entries: ConversationEntry[]): ConversationEntry[] {
  return [...entries].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}
interface UseConversationHistoryOptions {
  userId: string | null | undefined;
  currentSessionId: string;
  isConnected: boolean;
}
export function useConversationHistory({
  userId,
  currentSessionId,
  isConnected
}: UseConversationHistoryOptions) {
  const [conversations, setConversations] = useState<ConversationEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const fetchedRef = useRef(false);
  const fetchConversations = useCallback(async () => {
    if (!userId || !isConnected) return;
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const data = await apiClient.listConversations(userId);
      setConversations(sortConversationsByActivity(data.sessions ?? []));
    } catch (err) {
      // Surface a retryable failure instead of rendering "empty" — an empty list
      // reads as data loss. (live report, 2026-06-17: a backend timeout on
      // relogin made a user's history/files/canvas drawers all show empty.) We
      // keep any conversations already loaded rather than wiping them.
      console.warn('[ConversationHistory] Failed to fetch conversations:', err);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [userId, isConnected]);

  // Fetch on mount and when userId changes
  useEffect(() => {
    if (userId && isConnected) {
      fetchConversations();
      fetchedRef.current = true;
    }
  }, [userId, isConnected, fetchConversations]);

  // Re-fetch when currentSessionId changes (new conversation may have been created)
  useEffect(() => {
    if (fetchedRef.current && userId && isConnected) {
      // Debounce to avoid hammering on rapid session switches
      const timer = setTimeout(fetchConversations, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentSessionId, userId, isConnected, fetchConversations]);

  // Merge a single entry locally — used after registerConversation so we don't
  // need a follow-up listConversations round-trip just to see our own write.
  const upsertOne = useCallback((entry: ConversationEntry) => {
    setConversations(prev => {
      const idx = prev.findIndex(c => c.session_id === entry.session_id);
      const next = idx === -1 ? [entry, ...prev] : prev.map((c_0, i) => i === idx ? entry : c_0);
      return sortConversationsByActivity(next);
    });
  }, []);

  // Title-update event — fired by use-chat after auto-title resolves so the
  // sidebar reflects the new title without an extra listConversations roundtrip.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        sessionId?: string;
        title?: string;
      } | undefined;
      if (!detail?.sessionId || !detail.title) return;
      setConversations(prev_0 => {
        const idx_0 = prev_0.findIndex(c_1 => c_1.session_id === detail.sessionId);
        if (idx_0 === -1) return prev_0;
        const next_0 = prev_0.slice();
        // Title-only refresh — do not touch updated_at (activity sort key).
        next_0[idx_0] = {
          ...next_0[idx_0],
          title: detail.title!
        };
        return next_0;
      });
    };
    window.addEventListener('crucible:title-updated', handler);
    return () => window.removeEventListener('crucible:title-updated', handler);
  }, []);
  return useMemo(() => ({
    conversations,
    isLoading,
    loadFailed,
    refresh: fetchConversations,
    upsertOne
  }), [conversations, isLoading, loadFailed, fetchConversations, upsertOne]);
}